import { get, put } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useMutation, type UseMutationOptions } from "@tanstack/react-query";
import axios from "axios";
import { z } from "zod";

interface UpdateProfilePictureParams {
  file: File;
  workerId: string;
}

const presignedUrlResponseSchema = z.object({
  presigned_url: z.string(),
  fileStorageFileKey: z.string(),
});

/**
 * Fetches a presigned upload URL for a worker profile picture.
 */
async function fetchPresignedUrl(workerId: string) {
  const response = await get<unknown, z.infer<typeof presignedUrlResponseSchema>>({
    url: `${environmentConfig.REACT_APP_BASE_API_URL}/agentProfile/${workerId}/profile-image-presigned-upload-url`,
    queryParams: { extension: "jpeg" },
    responseSchema: presignedUrlResponseSchema,
  });

  return response.data;
}

export function useUpdateProfilePicture(
  options: UseMutationOptions<string, Error, UpdateProfilePictureParams> = {}
) {
  return useMutation({
    mutationFn: async ({ file, workerId }) => {
      const { presigned_url: presignedUrl, fileStorageFileKey } = await fetchPresignedUrl(workerId);

      // Upload the image directly to S3 using the presigned URL
      await axios.put(presignedUrl, file, {
        headers: {
          "Content-Type": "image/jpeg",
        },
      });

      // Notify backend that the profile image is now available
      await put({
        url: `${environmentConfig.REACT_APP_BASE_API_URL}/agentprofile/profilePic`,
        data: { profileImagefileStorageFileKey: fileStorageFileKey },
        responseSchema: z.unknown(),
      });

      logEvent(APP_V2_APP_EVENTS.PLACEMENT_CANDIDATE_PROFILE_PHOTO_UPLOADED, {
        workerId,
      });

      return fileStorageFileKey;
    },
    ...options,
  });
}
